import { signInWithEmailAndPassword } from 'firebase/auth'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { FC, useContext, useState } from 'react'
import { HiLockClosed, HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi'
import MiniFooter from '../../components/MiniFooter'
import * as ROUTES from '../../constants/routes'
import { signInWithGoogle } from '../../lib/authServices'
import { AuthContext } from '../../lib/context'

interface LoginProps {}

const Login: FC<LoginProps> = ({}) => {
  const auth = useContext(AuthContext)
  // const auth = getAuth()
  const router = useRouter()

  const initialState = {
    email: '',
    password: '',
    rememberme: false,
  }

  const [input, setInput] = useState(initialState)
  const [showPassword, setShowPassword] = useState(false)

  // Potential Error
  const [error, setError] = useState<string>('')

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setInput((prevInput) => ({
      ...prevInput,
      [name]: value,
    }))
  }

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    // await setPersistence(auth, browserSessionPersistence)

    try {
      const cred = await signInWithEmailAndPassword(auth, input.email, input.password)

      /**
       * Get the user token in order to check which role he/she has (admin, employee, patient)
       */
      const token = await cred.user.getIdToken()

      const response = await fetch('/api/getUserRole', {
        method: 'POST',
        body: JSON.stringify({ token }),
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const { admin, employee } = await response.json()

      if (admin === true || employee === true) {
        router.push('/admin')
      } else {
        router.push(ROUTES.DASHBOARD)
      }
    } catch (error: any) {
      console.log(error)
      setInput(initialState)
      setError(error.message)
    }
  }

  const handleGoogleLogin = async () => {
    try {
      const { admin, employee } = await signInWithGoogle()

      if (admin === true || employee === true) {
        router.push('/admin')
      } else {
        router.push(ROUTES.DASHBOARD)
      }
    } catch (error: any) {
      setError(error.message)
    }
  }

  return (
    <>
      <Head>
        <title>Login - Praxis am Bulkamp</title>
        <meta name='description' content='Zahnärzte Praxis am Bulkamp' />
        <link rel='icon' href='/logo-without-text.png' />
      </Head>
      <div className='min-h-screen bg-gray-50 flex flex-col items-center justify-around pb-6 pt-24 px-4 sm:px-6 lg:px-8'>
        <div className='max-w-md w-full space-y-8'>
          <div>
            <div className='relative mx-auto h-12 w-auto'>
              <Image
                quality={100}
                src='/logo-without-text.png'
                layout='fill'
                objectFit='contain'
                alt='Praxis Am Bulkamp Logo'
                className='pointer-events-none select-none'
              />
            </div>
            <h2 className='mt-6 text-center text-3xl font-extrabold text-gray-900'>
              Melden Sie sich&nbsp;an
            </h2>
            <p className='mt-2 text-center text-sm text-gray-600'>
              Sie haben keinen Account?{' '}
              <Link href={ROUTES.SIGNUP}>
                <a className='font-medium text-indigo-600 hover:text-indigo-500'>Account erstellen</a>
              </Link>
            </p>
          </div>
          <form onSubmit={handleLogin} className='mt-8 space-y-6'>
            <input type='hidden' name='remember' defaultValue='true' />
            <div className='rounded-md shadow-sm -space-y-px'>
              {error && <p className='text-sm text-red-500 mb-1'>{error}</p>}
              <div>
                <label htmlFor='email-address' className='sr-only'>
                  Email Adresse
                </label>
                <input
                  value={input.email}
                  onChange={handleInput}
                  id='email-address'
                  name='email'
                  type='email'
                  autoComplete='email'
                  required
                  className='appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm'
                  placeholder='Email Adresse'
                />
              </div>
              <div className='relative'>
                <label htmlFor='password' className='sr-only'>
                  Passwort
                </label>
                <input
                  value={input.password}
                  onChange={handleInput}
                  id='password'
                  name='password'
                  type={showPassword ? 'text' : 'password'}
                  autoComplete='current-password'
                  required
                  className='appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm'
                  placeholder='Passwort'
                />
                <span className='absolute z-30 cursor-pointer right-0 inset-y-0 flex items-center pr-3'>
                  {showPassword ? (
                    <HiOutlineEyeOff
                      className='h-5 w-5 text-gray-400 transition hover:text-gray-500'
                      aria-hidden='true'
                      onClick={() => setShowPassword((prev) => !prev)}
                    />
                  ) : (
                    <HiOutlineEye
                      className='h-5 w-5 text-gray-400 transition hover:text-gray-500'
                      aria-hidden='true'
                      onClick={() => setShowPassword((prev) => !prev)}
                    />
                  )}
                </span>
              </div>
            </div>

            <div className='flex items-center justify-center'>
              <div className='text-sm'>
                <Link href='/myaccount/forgot-password'>
                  <a className='font-medium text-indigo-600 hover:text-indigo-500'>Passwort vergessen?</a>
                </Link>
              </div>
            </div>

            <div>
              <button
                type='submit'
                className='group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'>
                <span className='absolute left-0 inset-y-0 flex items-center pl-3'>
                  <HiLockClosed
                    className='h-5 w-5 text-indigo-500 group-hover:text-indigo-400'
                    aria-hidden='true'
                  />
                </span>
                Anmelden
              </button>
            </div>
          </form>
          <div className='flex justify-center items-center gap-4'>
            <hr className='border-gray-300 w-12' />
            <p className='text-gray-500 text-sm'>ODER</p>
            <hr className='border-gray-300 w-12' />
            <hr />
          </div>
          <button
            type='button'
            onClick={handleGoogleLogin}
            className='group relative w-full outline outline-[1px] outline-gray-300 flex justify-center py-4 px-4 border border-transparent text-sm font-medium rounded-md text-gray-800 bg-white hover:bg-gray-100/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'>
            <div className='relative h-5 w-5 mr-4'>
              <Image src='/logo-google.png' layout='fill' objectFit='contain' />
            </div>
            Mit Google anmelden
          </button>
        </div>

        <MiniFooter />
      </div>
    </>
  )
}

export default Login
